import { useState, useCallback, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";

import { CampaignSelect } from "@/features/campaigns";
import { CreatorSelect } from "@/features/creators/components/CreatorSelect";
import { apiSlice } from "@/app/apiSlice";
import { showToast } from "components/Toasts/helpers/showToast";

interface CreateDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  creatorId?: string;
  campaignId?: string;
  onDocumentSaved?: () => void;
}

const validationSchema = Yup.object({
  files: Yup.array()
    .min(1, "A file is required")
    .test("file-size", "File size must be less than 200MB", function (files) {
      if (!files || files.length === 0) return true;
      const maxSize = 200 * 1024 * 1024; // 200MB in bytes
      return files.every((file: File) => file.size <= maxSize);
    })
    .required("A file is required"),
  title: Yup.string().optional(),
  campaign_id: Yup.string().test(
    "one-of-required",
    "Either a campaign or creator must be selected",
    function (campaign_id) {
      const { user_id } = this.parent;
      const hasCampaign = !!campaign_id;
      const hasCreator = !!user_id;
      return hasCampaign || hasCreator;
    },
  ),
  user_id: Yup.string().test(
    "one-of-required",
    "Either a campaign or creator must be selected",
    function (user_id) {
      const { campaign_id } = this.parent;
      const hasCampaign = !!campaign_id;
      const hasCreator = !!user_id;
      return hasCampaign || hasCreator;
    },
  ),
});

export const CreateDocumentModal = ({
  isOpen,
  onClose,
  creatorId,
  campaignId,
  onDocumentSaved,
}: CreateDocumentModalProps) => {
  const [createDocument, { isLoading: isCreating }] =
    apiSlice.useCreateDocumentMutation();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = async (values: any, actions: any) => {
    try {
      // Filter out empty values to avoid sending unnecessary fields
      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([key, value]) => {
          if (key === "files") return true; // Always include files
          if (value === null || value === undefined) return false;
          if (typeof value === "string" && value.trim() === "") return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        }),
      );

      const { result, error } = await createDocument(filteredValues).unwrap();
      if (error) throw new Error(error);
      showToast({
        type: "success",
        message: "Document uploaded successfully",
      });

      actions.setSubmitting(false);
      onDocumentSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving document -", error);
      showToast({
        type: "error",
        message: "Error saving document",
      });
      actions.setSubmitting(false);
    }
  };

  const initialValues = useMemo(
    () => ({
      files: [],
      title: "",
      campaign_id: campaignId || "",
      user_id: creatorId || "",
    }),
    [campaignId, creatorId],
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" backdrop="static">
      <ModalHeader toggle={handleClose}>Upload Document</ModalHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          validateForm,
          errors,
          touched,
          submitForm,
          isSubmitting,
        }) => (
          <>
            <ModalBody>
              <Form>
                <Row>
                  {/* File Upload Field */}
                  <Col md={12}>
                    <FormGroup>
                      <Label htmlFor="files">Document/File *</Label>
                      <Input
                        type="file"
                        name="files"
                        id="files"
                        multiple
                        accept="*/*"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setFieldValue("files", files);
                        }}
                        invalid={touched.files && !!errors.files}
                      />
                      <ErrorMessage name="files" component={FormFeedback} />
                    </FormGroup>
                  </Col>

                  {/* Title Field */}
                  <Col md={12}>
                    <FormGroup>
                      <Label htmlFor="title">Title (optional)</Label>
                      <Input
                        type="text"
                        name="title"
                        id="title"
                        value={values.title}
                        onChange={(e) => {
                          setFieldValue("title", e.target.value);
                          setFieldTouched("title", true);
                        }}
                        onBlur={() => setFieldTouched("title", true)}
                        placeholder="Leave blank to use filename"
                        invalid={touched.title && !!errors.title}
                      />
                      <ErrorMessage name="title" component={FormFeedback} />
                    </FormGroup>
                  </Col>

                  {/* Campaign Select */}
                  <Col md={6}>
                    <FormGroup>
                      <Label htmlFor="campaign_id">Campaign</Label>
                      <CampaignSelect
                        name="campaign_id"
                        required={false}
                        onChange={async (selectedOption) => {
                          const newCampaignId = selectedOption?.value || "";
                          await setFieldValue("campaign_id", newCampaignId);
                          setFieldTouched("campaign_id", true);
                          // Clear creator selection when campaign is selected
                          if (newCampaignId) {
                            await setFieldValue("user_id", "");
                            setFieldTouched("user_id", false);
                          }
                          // Validate the form after field values are updated
                          await validateForm();
                        }}
                      />
                      <ErrorMessage
                        name="campaign_id"
                        component={FormFeedback}
                      />
                    </FormGroup>
                  </Col>

                  {/* Creator Select */}
                  <Col md={6}>
                    <FormGroup>
                      <Label htmlFor="user_id">Creator</Label>
                      <CreatorSelect
                        name="user_id"
                        required={false}
                        onChange={async (selectedOption) => {
                          const newUserId = selectedOption?.value || "";
                          await setFieldValue("user_id", newUserId);
                          setFieldTouched("user_id", true);
                          // Clear campaign selection when creator is selected
                          if (newUserId) {
                            await setFieldValue("campaign_id", "");
                            setFieldTouched("campaign_id", false);
                          }
                          // Validate the form after field values are updated
                          await validateForm();
                        }}
                      />
                      <ErrorMessage name="user_id" component={FormFeedback} />
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </ModalBody>

            <ModalFooter>
              <Button color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={submitForm}
                disabled={isSubmitting || isCreating}
              >
                {isCreating ? "Uploading..." : "Upload Document"}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  );
};
